import { useTranslation } from 'react-i18next';
import { Button, Modal, Select } from '@/components/ui';
import { useOrganizationOptions, useOrgLevelOptions } from '../Shared/useOrgLevelPicker';
import type { AssignLevelToAccountVM } from '../useAssignLevelToAccount';

/**
 * EAA-1820 (frame 84, "Gán Level"): org picker, then that org's Level picker —
 * see `useAssignLevelToAccount` for why this always CREATES a new grant rather
 * than editing one.
 *
 * EAA-1804: footer swapped to the kit `Button` instead of hand-written
 * `<button className="portal-btn ...">`.
 */
export function AssignLevelToAccountDialog({ vm }: { vm: AssignLevelToAccountVM }) {
    const { t } = useTranslation();
    const open = Boolean(vm.target);
    const orgs = useOrganizationOptions(open);
    const levels = useOrgLevelOptions(vm.orgId, open);
    const identity = vm.target?.email || vm.target?.username || '';

    return (
        // EAA-1804: root debugging handle, matching the folder's existing acc-* prefix.
        <Modal
            className="acc-assign-level-dialog"
            open={open}
            onCancel={vm.close}
            title={t('console.accounts.assignLevel.title')}
            footer={
                <>
                    <Button onClick={vm.close} disabled={vm.busy}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="primary"
                        onClick={vm.submit}
                        disabled={vm.busy || !vm.orgId || !vm.levelId}
                    >
                        {vm.busy ? t('console.accounts.assignLevel.submitBusy') : t('console.accounts.assignLevel.submitButton')}
                    </Button>
                </>
            }
        >
            <p className="portal-admin-hint">{t('console.accounts.assignLevel.hint', { identity })}</p>
            <div className="portal-admin-form">
                <label className="sa-dialog-field">
                    {t('console.accounts.assignLevel.orgLabel')}
                    <Select
                        value={vm.orgId || undefined}
                        onChange={(next) => vm.setOrgId((next as string) ?? '')}
                        options={orgs.options}
                        loading={orgs.loading}
                        placeholder={t('console.accounts.assignLevel.orgPlaceholder')}
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%' }}
                    />
                </label>
                <label className="sa-dialog-field">
                    {t('console.accounts.assignLevel.levelLabel')}
                    <Select
                        value={vm.levelId || undefined}
                        onChange={(next) => vm.setLevelId((next as string) ?? '')}
                        options={levels.options}
                        loading={levels.loading}
                        disabled={!vm.orgId}
                        placeholder={
                            vm.orgId
                                ? t('console.accounts.assignLevel.levelPlaceholder')
                                : t('console.accounts.assignLevel.levelNeedsOrgPlaceholder')
                        }
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%' }}
                    />
                </label>
                {vm.orgId && !levels.loading && levels.options.length === 0 && (
                    <p className="portal-admin-hint">{t('console.accounts.assignLevel.noLevelsHint')}</p>
                )}
                {vm.error && <p className="portal-admin-error">{vm.error}</p>}
            </div>
        </Modal>
    );
}

export default AssignLevelToAccountDialog;
